import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  fetchSubscribers,
  exportSubscribers,
  deleteSubscriber,
  FilterOperator,
} from "@/store/altingross/news-letter-subscriber";
import {
  generateAiTemplate,
  createCampaign,
  resetCampaignState,
  fetchCampaign,
  resetDetail,
} from "@/store/altingross/campaigns";
import PageHeader from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Search, Send, Mail, Code2, Eye, Layout, Sparkles, Download,
  Wand2, Loader2, X, Tag, Link2, Megaphone, Star, Globe,
  Maximize2, Info, ChevronLeft, ChevronRight, XCircle, Trash2, CheckCircle2, User,
} from "lucide-react";
import { toast } from "sonner";
import Swal from "sweetalert2";
import { cn } from "@/lib/utils";
import GuestDetailModal from "@/components/shared/GuestDetailModal";

// ─── Templates ────────────────────────────────────────────────────────────
const TEMPLATES = {
  bos: {
    label: "Boş",
    html: `<p>Merhaba,</p>\n<p>Mesajınızı buraya yazın...</p>\n<p>Saygılarımızla,<br/>Altıngross Ekibi</p>`,
  },
  duyuru: {
    label: "Duyuru",
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px; background:#ffffff;">
  <div style="background: linear-gradient(135deg,#2563EB,#3B82F6); color:#fff; padding:32px 24px; border-radius:8px 8px 0 0;">
    <h1 style="margin:0; font-size:24px;">Önemli Duyuru</h1>
    <p style="margin:8px 0 0; opacity:.9; font-size:14px;">Altıngross sizi bilgilendirmek istiyor</p>
  </div>
  <div style="padding:24px; border:1px solid #E2E8F0; border-top:none; border-radius:0 0 8px 8px; color:#334155; line-height:1.6;">
    <p>Değerli Müşterimiz,</p>
    <p>Buraya duyuru içeriğinizi yazınız.</p>
    <p><a href="https://www.altingross.com" style="display:inline-block; background:#2563EB; color:#fff; padding:12px 24px; text-decoration:none; border-radius:6px; font-weight:600;">Detayları Gör</a></p>
    <p style="font-size:12px; color:#64748B; margin-top:24px;">Saygılarımızla,<br/>Altıngross Ekibi</p>
  </div>
</div>`,
  },
  kampanya: {
    label: "Kampanya",
    html: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 24px; background:#F8FAFC;">
  <div style="background:#fff; border-radius:8px; overflow:hidden; border:1px solid #E2E8F0;">
    <div style="background:#2563EB; color:#fff; padding:40px 24px; text-align:center;">
      <div style="font-size:14px; letter-spacing:2px; opacity:.85;">ÖZEL KAMPANYA</div>
      <h1 style="margin:8px 0; font-size:36px;">%25 İndirim</h1>
      <p style="margin:0; opacity:.9;">Sadece bu hafta sonu geçerli</p>
    </div>
    <div style="padding:32px 24px; color:#334155; line-height:1.6;">
      <p>Merhaba,</p>
      <p>Tüm ürünlerde %25 indirim fırsatını değerlendirin.</p>
      <p style="text-align:center; margin:24px 0;">
        <a href="https://www.altingross.com" style="display:inline-block; background:#2563EB; color:#fff; padding:14px 32px; text-decoration:none; border-radius:6px; font-weight:600;">Alışverişe Başla</a>
      </p>
    </div>
  </div>
</div>`,
  },
};

const renderHtmlPreview = (subject, preheader, html) => `<!DOCTYPE html>
<html><head><meta charset="utf-8"/><title>${subject}</title>
<style>body{margin:0;background:#F0F4F8;padding:24px;font-family:system-ui,sans-serif;color:#334155;}</style>
</head><body>
${preheader ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;color:transparent;">${preheader}</div>` : ""}
${html}
</body></html>`;

const VOID_TAGS = new Set(["area","base","br","col","embed","hr","img","input","link","meta","param","source","track","wbr"]);
function formatHtml(html) {
  const normalized = html.replace(/>\s+</g, "><").replace(/></g, ">\n<").trim();
  let depth = 0;
  return normalized.split("\n").reduce((acc, raw) => {
    const line = raw.trim();
    if (!line) return acc;
    const isClosing = /^<\/[a-zA-Z]/.test(line);
    const isOpening = /^<[a-zA-Z]/.test(line) && !isClosing && !line.endsWith("/>");
    const tagName = (line.match(/^<\/?([a-zA-Z0-9-]+)/) || [])[1]?.toLowerCase();
    const hasInlineClose = isOpening && tagName && line.includes(`</${tagName}>`);
    if (isClosing) depth = Math.max(0, depth - 1);
    acc.push("  ".repeat(depth) + line);
    if (isOpening && tagName && !VOID_TAGS.has(tagName) && !hasInlineClose) depth++;
    return acc;
  }, []).join("\n");
}

function extractLinks(html) {
  if (typeof window === "undefined") return [];
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, "text/html");
  return Array.from(doc.querySelectorAll("a")).map((a, i) => ({
    i,
    text: a.textContent.trim().slice(0, 60) || "(metin yok)",
    href: a.getAttribute("href") || "",
  }));
}
function applyLinks(html, links) {
  if (typeof window === "undefined") return html;
  const wrapper = document.createElement("div");
  wrapper.innerHTML = html;
  wrapper.querySelectorAll("a").forEach((a, i) => { if (links[i]) a.setAttribute("href", links[i].href); });
  return wrapper.innerHTML;
}

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

const PAGE_SIZE = 20;

const parseTags = (tags) =>
  tags ? tags.split(",").map((t) => t.trim()).filter(Boolean) : [];

// ─── Yup schema ──────────────────────────────────────────────────────────
const campaignSchema = Yup.object({
  name:              Yup.string().required("Kampanya adı zorunludur"),
  subject:           Yup.string().required("Konu alanı boş bırakılamaz").max(120, "En fazla 120 karakter"),
  htmlContent:       Yup.string().required("Mail içeriği boş bırakılamaz"),
  previewText:       Yup.string(),
  groqAiPrompt:      Yup.string(),
  tags:              Yup.array().of(Yup.string()),
  isVisibleOnWebsite:       Yup.boolean(),
  isOnlyForVerifiedEmails:  Yup.boolean(),
});

const INITIAL_FORM = {
  name:              "",
  subject:           "",
  htmlContent:       TEMPLATES.duyuru.html,
  previewText:       "",
  groqAiPrompt:      "",
  tags:              ["kampanya"],
  isVisibleOnWebsite:      false,
  isOnlyForVerifiedEmails: false,
};

export default function NewsletterSubscribers() {
  const dispatch = useDispatch();
  const { id: campaignId } = useParams();
  const navigate = useNavigate();
  const { data: listData, loading, error } = useSelector((s) => s.newsLetterSubscriber.list);
  const exportLoading  = useSelector((s) => s.newsLetterSubscriber.export.loading);
  const { aiLoading, saveLoading, sendLoading } = useSelector((s) => s.campaigns);
  const detail = useSelector((s) => s.campaigns.detail);

  const items = listData?.items ?? [];

  // Pagination
  const [page, setPage] = useState(1);

  // Filter
  const [emailSearch, setEmailSearch]     = useState("");
  const [confirmFilter, setConfirmFilter] = useState("Tümü");
  const [appliedFilter, setAppliedFilter] = useState(null);

  // Composer UI state
  const [composerOpen, setComposerOpen]         = useState(false);
  const [tagInput, setTagInput]                 = useState("");
  const [aiPanelOpen, setAiPanelOpen]           = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [linksModalOpen, setLinksModalOpen]     = useState(false);
  const [linksDraft, setLinksDraft]             = useState([]);
  const [guestModalOpen, setGuestModalOpen]     = useState(false);

  // ── Formik ───────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: INITIAL_FORM,
    validationSchema: campaignSchema,
    validateOnChange: false,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        const body = { ...values, tags: values.tags.join(",") };
        await dispatch(createCampaign(body)).unwrap();
        toast.success(`"${values.name}" kampanyası kaydedildi`);
        handleComposerClose();
      } catch {
        toast.error("Kampanya kaydedilemedi");
      }
    },
  });

  // ── Fetch ────────────────────────────────────────────────────────────────
  useEffect(() => {
    dispatch(fetchSubscribers({ page, size: PAGE_SIZE, filter: appliedFilter }));
  }, [dispatch, page, appliedFilter]);

  // ── Campaign copy: URL param ile geldiyse kampanyayı yükle & modal aç ──
  useEffect(() => {
    if (campaignId) {
      dispatch(fetchCampaign(campaignId));
      setComposerOpen(true);
    }
  }, [campaignId, dispatch]);

  useEffect(() => {
    if (campaignId && detail.data && !detail.loading) {
      const c = detail.data;
      formik.setValues({
        name:               `Kopya: ${c.name}`,
        subject:            c.subject        || "",
        htmlContent:        c.htmlContent    || TEMPLATES.duyuru.html,
        previewText:        c.previewText    || "",
        groqAiPrompt:       c.groqAiPrompt   || "",
        tags:               parseTags(c.tags),
        isVisibleOnWebsite:      false,
        isOnlyForVerifiedEmails: c.isOnlyForVerifiedEmails ?? false,
      });
    }
  }, [detail.data, detail.loading, campaignId]);

  // ── Filter ───────────────────────────────────────────────────────────────
  const applyFilter = useCallback(() => {
    setPage(1);
    if (emailSearch.trim()) {
      setAppliedFilter({ field: "email", value: emailSearch.trim(), operator: FilterOperator.Contains });
    } else if (confirmFilter !== "Tümü") {
      setAppliedFilter({ field: "isConfirmed", value: confirmFilter, operator: FilterOperator.Equals });
    } else {
      setAppliedFilter(null);
    }
  }, [emailSearch, confirmFilter]);

  const clearFilter = useCallback(() => {
    setEmailSearch(""); setConfirmFilter("Tümü"); setAppliedFilter(null); setPage(1);
  }, []);

  // ── Export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    dispatch(exportSubscribers({ fileName: "aboneler", sheetName: "Bülten Aboneleri" }));
  };

  // ── Stats ────────────────────────────────────────────────────────────────
  const totalCount = listData?.count ?? 0;
  const totalPages = listData?.totalPages ?? 1;

  // ── Composer helpers ─────────────────────────────────────────────────────
  const handleComposerClose = () => {
    setComposerOpen(false);
    setAiPanelOpen(false);
    setTagInput("");
    formik.resetForm();
    dispatch(resetCampaignState());
    dispatch(resetDetail());
    if (campaignId) {
      navigate("/newsletter", { replace: true });
    }
  };

  const handleAiGenerate = () => {
    const prompt = formik.values.groqAiPrompt;
    if (!prompt.trim()) { toast.error("Lütfen bir prompt girin"); return; }
    dispatch(generateAiTemplate(prompt))
      .unwrap()
      .then((res) => {
        formik.setFieldValue("htmlContent", res?.htmlContent ?? "");
        toast.success("AI içeriği oluşturuldu");
      })
      .catch(() => toast.error("İçerik oluşturulamadı"));
  };

  const handleFormat = () => {
    try {
      formik.setFieldValue("htmlContent", formatHtml(formik.values.htmlContent));
      toast.success("HTML formatlandı");
    } catch { toast.error("HTML formatlanamadı"); }
  };

  const openLinksEditor = () => {
    setLinksDraft(extractLinks(formik.values.htmlContent));
    setLinksModalOpen(true);
  };

  const saveLinks = () => {
    formik.setFieldValue("htmlContent", applyLinks(formik.values.htmlContent, linksDraft));
    setLinksModalOpen(false);
    toast.success(`${linksDraft.length} link güncellendi`);
  };

  const addTag = (e) => {
    if (e.key === "Enter" && tagInput.trim()) {
      e.preventDefault();
      const t = tagInput.trim().toLowerCase();
      if (!formik.values.tags.includes(t))
        formik.setFieldValue("tags", [...formik.values.tags, t]);
      setTagInput("");
    }
  };

  // helpers
  const fv = formik.values;
  const fe = formik.errors;
  const ft = formik.touched;
  const err = (field) => ft[field] && fe[field]
    ? <p className="text-[11px] text-red-500 mt-1">{fe[field]}</p>
    : null;

  return (
    <div data-testid="newsletter-page">
      <PageHeader
        testId="newsletter-header"
        title="Bülten Aboneleri"
        description="Bültene kayıtlı kullanıcıları yönetin ve toplu mail kampanyası gönderin."
        action={
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-md" onClick={handleExport} disabled={exportLoading} data-testid="newsletter-export-button">
              {exportLoading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}
              Excel İndir
            </Button>
            <Button data-testid="newsletter-send-campaign-button" onClick={() => setComposerOpen(true)} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm shadow-blue-200">
              <Send className="h-4 w-4 mr-1.5" /> Mail Kampanyası Gönder
            </Button>
          </div>
        }
      />

      {/* Table card */}
      <div className="glass-card p-6">
        {/* Filter bar */}
        <div className="flex flex-col md:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="E-posta ara..." value={emailSearch} onChange={(e) => setEmailSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && applyFilter()} data-testid="newsletter-search-input" className="pl-10 h-10 bg-white rounded-md" />
          </div>
          <Select value={confirmFilter} onValueChange={setConfirmFilter}>
            <SelectTrigger className="w-full md:w-44 h-10 rounded-md bg-white" data-testid="newsletter-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Tümü">Tüm Durumlar</SelectItem>
              <SelectItem value="true">Onaylı</SelectItem>
              <SelectItem value="false">Onaysız</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={applyFilter} className="h-10 rounded-md bg-blue-600 hover:bg-blue-700 text-white" data-testid="newsletter-filter-apply">
            <Search className="h-4 w-4 mr-1.5" /> Ara
          </Button>
          {appliedFilter && (
            <Button onClick={clearFilter} variant="outline" className="h-10 rounded-md" data-testid="newsletter-filter-clear">
              <X className="h-4 w-4 mr-1.5" /> Temizle
            </Button>
          )}
        </div>

        {/* Table */}
        {error ? (
          <div className="text-center py-16 text-red-500 text-sm">Veriler yüklenirken bir hata oluştu.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-slate-100">
                <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">E-posta</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Kayıt Tarihi</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Onay Durumu</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Abonelik</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Misafir ID</TableHead>
                <TableHead className="w-16" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-14"><Loader2 className="h-6 w-6 animate-spin text-blue-500 mx-auto" /></TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-14">Abone bulunamadı</TableCell></TableRow>
              ) : items.map((s) => (
                <TableRow key={s.id} className="hover:bg-blue-50/40 border-slate-100" data-testid={`newsletter-row-${s.id}`}>
                  <TableCell className="font-medium text-slate-800">{s.email}</TableCell>
                  <TableCell className="text-sm text-slate-600">{formatDate(s.createdAt)}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className={s.isConfirmed ? "bg-blue-50 text-blue-700 border-blue-200 font-medium" : "bg-slate-100 text-slate-600 border-slate-200 font-medium"}>
                      {s.isConfirmed ? "Onaylı" : "Onaysız"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={s.unsubscribedAt ? "bg-red-50 text-red-600 border-red-200 font-medium" : "bg-emerald-50 text-emerald-700 border-emerald-200 font-medium"}>
                      {s.unsubscribedAt ? "Ayrılmış" : "Aktif"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {s.guestId ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        onClick={() => {
                          import("@/store/altingross/guests").then(({ getGuestById }) => {
                            dispatch(getGuestById(s.guestId));
                            setGuestModalOpen(true);
                          });
                        }}
                      >
                        <User className="h-3.5 w-3.5 mr-1" /> Misafir Bilgileri
                      </Button>
                    ) : (
                      <span className="text-xs text-slate-400 pl-3">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    {s.isConfirmed && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600 hover:bg-red-50"
                        onClick={async () => {
                          const result = await Swal.fire({ title: "Abone silinsin mi?", text: `${s.email} adresli abone kalıcı olarak silinecek.`, icon: "warning", showCancelButton: true, confirmButtonColor: "#dc2626", cancelButtonColor: "#6b7280", confirmButtonText: "Evet, sil", cancelButtonText: "Vazgeç" });
                          if (!result.isConfirmed) return;
                          dispatch(deleteSubscriber(s.id)).unwrap()
                            .then(() => toast.success("Abone silindi"))
                            .catch(() => toast.error("Silinemedi, tekrar deneyin"));
                        }}
                        data-testid={`newsletter-delete-${s.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        {/* Pagination */}
        {!loading && listData && totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-100">
            <div className="text-sm text-slate-500">
              Toplam <span className="font-semibold text-slate-700">{totalCount}</span> kayıt
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 rounded-md" disabled={!listData.hasPrev} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="newsletter-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-600 font-medium px-1">{page} / {totalPages}</span>
              <Button variant="outline" size="sm" className="h-8 rounded-md" disabled={!listData.hasNext} onClick={() => setPage((p) => p + 1)} data-testid="newsletter-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Misafir Detay Modal */}
      {guestModalOpen && (
        <GuestDetailModal 
          open={guestModalOpen} 
          onOpenChange={setGuestModalOpen} 
        />
      )}

      {/* ===== Composer Dialog ===== */}
      <Dialog open={composerOpen} onOpenChange={(v) => !v && handleComposerClose()}>
        <DialogContent data-testid="newsletter-composer-dialog" className="max-w-[1480px] w-[97vw] h-[94vh] p-0 gap-0 flex flex-col rounded-lg overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-slate-100 bg-white">
            <DialogTitle className="text-2xl flex items-center gap-2.5 font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <div className="h-9 w-9 rounded-md bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-sm shadow-blue-200">
                <Mail className="h-4.5 w-4.5 text-white" />
              </div>
              {campaignId ? "Kampanyayı Kopyala" : "Yeni Mail Kampanyası"}
            </DialogTitle>
            <DialogDescription className="ml-12 -mt-1">
              {campaignId
                ? "Seçili kampanya alanlarla dolduruldu. Düzenleyip kaydedin."
                : "Kampanyanızı oluşturun, HTML içeriğini düzenleyin ve önizleyin."}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={formik.handleSubmit} className="flex-1 overflow-hidden flex flex-col">
            {campaignId && detail.loading && (
              <div className="flex items-center gap-2 px-6 py-2.5 bg-blue-50 border-b border-blue-100 text-blue-700 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                Kampanya verileri yükleniyor...
              </div>
            )}
            <div className="flex-1 overflow-hidden flex flex-col lg:flex-row">

              {/* Left panel */}
              <div className="lg:w-[380px] lg:border-r border-slate-100 bg-slate-50/60 overflow-y-auto shrink-0">

                {/* Kampanya bilgileri */}
                <div className="p-5 border-b border-slate-200/70 space-y-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Mail className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-800 font-bold">Kampanya Bilgileri</span>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                      Kampanya Adı <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="name" name="name"
                      value={fv.name}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Şubat İndirim Kampanyası"
                      className={cn("h-10 bg-white rounded-md", ft.name && fe.name && "border-red-400 focus-visible:ring-red-300")}
                      data-testid="composer-name-input"
                    />
                    {err("name")}
                    <div className="text-[11px] text-slate-500 flex items-center gap-1">
                      <Info className="h-3 w-3" /> Yalnızca dahili tanımlama için kullanılır
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
                      Konu <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="subject" name="subject"
                      value={fv.subject}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      placeholder="Mail kutusunda görünen başlık"
                      maxLength={120}
                      className={cn("h-10 bg-white rounded-md", ft.subject && fe.subject && "border-red-400 focus-visible:ring-red-300")}
                      data-testid="composer-subject-input"
                    />
                    {err("subject")}
                    <div className="flex items-center justify-between text-[11px]">
                      <span className={cn("font-medium", (fv.subject ?? "").length > 70 ? "text-amber-600" : (fv.subject ?? "").length > 40 ? "text-blue-600" : "text-slate-500")}>
                        {(fv.subject ?? "").length === 0 ? "Önerilen: 30–60 karakter" : (fv.subject ?? "").length < 30 ? "Çok kısa olabilir" : (fv.subject ?? "").length <= 60 ? "İdeal uzunluk" : (fv.subject ?? "").length <= 80 ? "Mobilde kırpılabilir" : "Çok uzun"}
                      </span>
                      <span className="text-slate-400 font-mono">{(fv.subject ?? "").length}/120</span>
                    </div>
                    <div className="h-1 bg-slate-200 rounded-full overflow-hidden">
                      <div className={cn("h-full transition-all", (fv.subject ?? "").length > 80 ? "bg-amber-500" : "bg-blue-600")} style={{ width: `${Math.min(((fv.subject ?? "").length / 80) * 100, 100)}%` }} />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase tracking-wider text-slate-600 font-semibold">Önizleme Metni</Label>
                    <Input
                      id="previewText" name="previewText"
                      value={fv.previewText}
                      onChange={formik.handleChange}
                      placeholder="Mail kutusunda konunun altında görünen açıklama"
                      className="h-10 bg-white rounded-md"
                      data-testid="composer-preview-text-input"
                    />
                    <div className="text-[11px] text-slate-500">Mobil mail kutularında %30 daha fazla açılma sağlar.</div>
                  </div>
                </div>

                {/* Şablon */}
                <div className="p-5 border-b border-slate-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <Layout className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-800 font-bold">Hızlı Şablon</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { key: "bos",      icon: Mail,     color: "text-slate-600" },
                      { key: "duyuru",   icon: Megaphone,color: "text-blue-600"  },
                      { key: "kampanya", icon: Star,      color: "text-amber-600" },
                    ].map(({ key, icon: Icon, color }) => (
                      <button type="button" key={key}
                        onClick={() => { formik.setFieldValue("htmlContent", TEMPLATES[key].html); toast.info(`${TEMPLATES[key].label} şablonu yüklendi`); }}
                        data-testid={`composer-template-${key}`}
                        className="px-2 py-2.5 rounded-md border border-slate-200 bg-white hover:border-blue-300 hover:bg-blue-50/40 transition-colors flex flex-col items-center gap-1 group"
                      >
                        <Icon className={cn("h-4 w-4 group-hover:scale-110 transition-transform", color)} />
                        <span className="text-[11px] font-semibold text-slate-700">{TEMPLATES[key].label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Etiketler */}
                <div className="p-5 border-b border-slate-200/70">
                  <div className="flex items-center gap-2 mb-3">
                    <Tag className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-800 font-bold">Etiketler</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {fv.tags.map((t) => (
                      <span key={t} className="inline-flex items-center gap-1 pl-2 pr-1 h-6 rounded-md bg-blue-100 text-blue-700 text-xs font-medium" data-testid={`composer-tag-${t}`}>
                        #{t}
                        <button type="button" onClick={() => formik.setFieldValue("tags", fv.tags.filter((x) => x !== t))} className="h-4 w-4 rounded hover:bg-blue-200 inline-flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={addTag} placeholder="Etiket ekleyin ve Enter'a basın" className="h-9 bg-white rounded-md text-sm" data-testid="composer-tag-input" />
                </div>

                {/* Yayın ayarları */}
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Globe className="h-3.5 w-3.5 text-blue-600" />
                    <span className="text-[11px] uppercase tracking-widest text-slate-800 font-bold">Yayın Ayarları</span>
                  </div>
                  <label className="flex items-start gap-3 p-3 rounded-md bg-white border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors">
                    <Switch checked={fv.isVisibleOnWebsite} onCheckedChange={(v) => formik.setFieldValue("isVisibleOnWebsite", v)} data-testid="composer-visibility-switch" className="data-[state=checked]:bg-blue-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                        <Globe className="h-3.5 w-3.5 text-slate-400" /> Web Sitesinde Göster
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Bu kampanya site arşivinde herkese açık olarak yayınlansın.</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 rounded-md bg-white border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors mt-2">
                    <Switch checked={fv.isOnlyForVerifiedEmails} onCheckedChange={(v) => formik.setFieldValue("isOnlyForVerifiedEmails", v)} className="data-[state=checked]:bg-blue-600 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5 text-slate-400" /> Yalnızca Onaylananlar
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5">Aktif olursa yalnızca e-postasını onaylamış abonelere gönderilir.</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Right: editor */}
              <div className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50/60">
                <div className="px-5 py-3 border-b border-slate-100 bg-white flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <Code2 className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-slate-800">İçerik Editörü</span>
                    {ft.htmlContent && fe.htmlContent && (
                      <span className="text-[11px] text-red-500 font-medium">{fe.htmlContent}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Button type="button" onClick={handleFormat} variant="outline" size="sm" data-testid="composer-format-button" className="h-9 rounded-md text-xs font-semibold border-slate-200 hover:border-blue-300 hover:text-blue-700">
                      <Code2 className="h-3.5 w-3.5 mr-1.5" /> Formatla
                    </Button>
                    <Button type="button" onClick={() => setPreviewModalOpen(true)} variant="outline" size="sm" data-testid="composer-preview-button" className="h-9 rounded-md text-xs font-semibold border-slate-200 hover:border-blue-300 hover:text-blue-700">
                      <Maximize2 className="h-3.5 w-3.5 mr-1.5" /> Önizle
                    </Button>
                    <Button type="button" onClick={openLinksEditor} variant="outline" size="sm" data-testid="composer-links-button" className="h-9 rounded-md text-xs font-semibold border-slate-200 hover:border-blue-300 hover:text-blue-700">
                      <Link2 className="h-3.5 w-3.5 mr-1.5" /> Linkler
                    </Button>
                    <div className="w-px h-6 bg-slate-200 mx-1" />
                    <Button type="button" onClick={() => setAiPanelOpen((v) => !v)} size="sm" data-testid="composer-ai-toggle-button" className={cn("rounded-md h-9 text-xs font-semibold transition-all", aiPanelOpen ? "bg-blue-700 hover:bg-blue-800 text-white" : "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white shadow-sm shadow-blue-200")}>
                      <Wand2 className="h-3.5 w-3.5 mr-1.5" />
                      {aiPanelOpen ? "AI Panelini Kapat" : "GROQ AI ile Oluştur"}
                    </Button>
                  </div>
                </div>

                {/* AI panel */}
                {aiPanelOpen && (
                  <div className="border-b border-slate-100 bg-blue-50/40 px-5 py-4 animate-float-in">
                    <div className="flex items-start gap-3">
                      <div className="h-9 w-9 rounded-md bg-gradient-to-br from-blue-600 to-blue-400 flex items-center justify-center shadow-sm shrink-0">
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1.5">
                          <Label className="text-[10px] uppercase tracking-widest text-blue-900 font-semibold">GROQ AI Mail Üretici</Label>
                          <button type="button" onClick={() => setAiPanelOpen(false)} className="text-slate-400 hover:text-slate-600" data-testid="composer-ai-close-button">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        <Textarea
                          id="groqAiPrompt" name="groqAiPrompt"
                          value={fv.groqAiPrompt}
                          onChange={formik.handleChange}
                          placeholder="Ne tür bir mail oluşturayım? Örn: 'Black Friday'de tüm ürünlerde %50 indirim kampanyası'"
                          data-testid="composer-ai-prompt-textarea"
                          rows={3}
                          className="bg-white border-blue-200 focus-visible:ring-blue-500 rounded-md text-sm resize-none"
                        />
                        <div className="flex items-center justify-end gap-3 mt-3">
                          <Button type="button" onClick={handleAiGenerate} disabled={aiLoading || !fv.groqAiPrompt.trim()} data-testid="composer-ai-generate-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-md h-9 text-xs font-semibold">
                            {aiLoading ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Oluşturuluyor...</> : <><Wand2 className="h-3.5 w-3.5 mr-1.5" />HTML Üret</>}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Editor + preview */}
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 min-h-0 overflow-hidden">
                  <div className="flex flex-col min-h-0 lg:border-r border-slate-200 bg-slate-900">
                    <div className="px-4 py-2 bg-slate-800 border-b border-slate-700 flex items-center gap-2">
                      <Code2 className="h-3.5 w-3.5 text-blue-300" />
                      <span className="text-[11px] uppercase tracking-widest text-blue-200 font-semibold">HTML Kaynak</span>
                      <span className="ml-auto text-[10px] text-slate-400 font-mono">{(fv.htmlContent ?? "").length} karakter</span>
                    </div>
                    <Textarea
                      id="htmlContent" name="htmlContent"
                      value={fv.htmlContent}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      data-testid="composer-html-textarea"
                      spellCheck={false}
                      className="flex-1 font-mono text-[12.5px] bg-slate-900 text-slate-100 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none leading-relaxed resize-none p-4"
                      placeholder="<p>Mail HTML içeriği...</p>"
                    />
                  </div>
                  <div className="flex flex-col min-h-0 bg-slate-100 border-t lg:border-t-0 border-slate-200">
                    <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-2">
                      <Eye className="h-3.5 w-3.5 text-blue-600" />
                      <span className="text-[11px] uppercase tracking-widest text-slate-600 font-semibold">Canlı Önizleme</span>
                      <span className="ml-auto text-[10px] text-slate-500 truncate max-w-[60%] font-semibold">{fv.subject || "(konu yok)"}</span>
                    </div>
                    <iframe srcDoc={renderHtmlPreview(fv.subject, fv.previewText, fv.htmlContent)} title="Mail Önizleme" data-testid="composer-preview-iframe" className="flex-1 w-full bg-white" />
                  </div>
                </div>
              </div>
            </div>

            <DialogFooter className="px-6 py-3.5 border-t border-slate-100 bg-white !flex !flex-row !justify-between !space-x-0 items-center">
              <div className="flex items-center gap-3 text-sm">
                {fv.isVisibleOnWebsite && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md font-medium">
                    <Globe className="h-3 w-3" /> Sitede görünür
                  </span>
                )}
                {fv.tags.length > 0 && <span className="text-xs text-slate-500">{fv.tags.length} etiket</span>}
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={handleComposerClose} data-testid="composer-cancel-button" className="rounded-md">İptal</Button>
                <Button type="submit" disabled={saveLoading || sendLoading} data-testid="composer-send-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded-md shadow-sm shadow-blue-200">
                  {saveLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Kaydediliyor...</>
                    : sendLoading ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Gönderiliyor...</>
                    : <><Send className="h-4 w-4 mr-1.5" />Kampanyayı Kaydet</>}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Full preview modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent data-testid="preview-modal" className="max-w-[900px] w-[95vw] h-[88vh] p-0 gap-0 flex flex-col rounded-lg overflow-hidden">
          <DialogHeader className="px-5 py-3 border-b border-slate-100 bg-white">
            <DialogTitle className="text-base flex items-center gap-2 font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Eye className="h-4 w-4 text-blue-600" /> Mail Önizleme
            </DialogTitle>
            <DialogDescription className="text-xs">Mail kutusunda görüneceği şekilde tam ekran önizleme</DialogDescription>
          </DialogHeader>
          <div className="bg-slate-100 px-5 py-3 border-b border-slate-200">
            <div className="text-[11px] text-slate-500 leading-relaxed">
              <div><span className="font-semibold text-slate-700">Konu:</span> {fv.subject || "—"}</div>
              <div><span className="font-semibold text-slate-700">Önizleme metni:</span> {fv.previewText || "—"}</div>
            </div>
          </div>
          <iframe srcDoc={renderHtmlPreview(fv.subject, fv.previewText, fv.htmlContent)} title="Tam Ekran Önizleme" className="flex-1 w-full bg-white" data-testid="preview-modal-iframe" />
        </DialogContent>
      </Dialog>

      {/* Links modal */}
      <Dialog open={linksModalOpen} onOpenChange={setLinksModalOpen}>
        <DialogContent data-testid="links-modal" className="max-w-[720px] w-[95vw] max-h-[85vh] p-0 gap-0 flex flex-col rounded-lg overflow-hidden">
          <DialogHeader className="px-5 py-4 border-b border-slate-100 bg-white">
            <DialogTitle className="text-base flex items-center gap-2 font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <Link2 className="h-4 w-4 text-blue-600" /> Linkleri Yönet
            </DialogTitle>
            <DialogDescription className="text-xs">
              HTML içindeki tüm <code className="px-1 bg-slate-100 rounded text-blue-700">&lt;a&gt;</code> taglerinin <code className="px-1 bg-slate-100 rounded text-blue-700">href</code> değerlerini düzenleyin.
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 py-4 bg-slate-50/60">
            {linksDraft.length === 0 ? (
              <div className="text-center py-10 text-slate-500 text-sm">
                <Link2 className="h-10 w-10 text-slate-300 mx-auto mb-2" /> Düzenlenebilecek link bulunamadı.
              </div>
            ) : (
              <div className="space-y-3">
                {linksDraft.map((l, idx) => (
                  <div key={idx} className="bg-white border border-slate-200 rounded-md p-3" data-testid={`link-row-${idx}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-flex items-center justify-center h-5 min-w-5 px-1.5 rounded text-[10px] font-bold bg-blue-100 text-blue-700">#{idx + 1}</span>
                      <span className="text-sm font-medium text-slate-800 truncate">{l.text}</span>
                    </div>
                    <Input value={l.href} onChange={(e) => { const copy = [...linksDraft]; copy[idx] = { ...copy[idx], href: e.target.value }; setLinksDraft(copy); }} placeholder="https://..." className="h-9 bg-white rounded-md font-mono text-xs" data-testid={`link-href-input-${idx}`} />
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="px-5 py-3 border-t border-slate-100 bg-white !flex !flex-row !justify-between !space-x-0 items-center">
            <div className="text-xs text-slate-500">Toplam <span className="font-semibold text-blue-700">{linksDraft.length}</span> link</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setLinksModalOpen(false)} className="rounded-md" data-testid="links-cancel-button">İptal</Button>
              <Button onClick={saveLinks} disabled={linksDraft.length === 0} className="bg-blue-600 hover:bg-blue-700 text-white rounded-md" data-testid="links-save-button">Linkleri Güncelle</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
